import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  inject
} from '@angular/core';
import { ActivatedRoute, Navigation, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FilterModel, IServerSideGetRowsRequest } from 'ag-grid-enterprise';
import { merge } from 'lodash';
import { forkJoin, from } from 'rxjs';
import { ClientVendorSearchModel } from 'src/app/classes/client-vendor-search-model';
import {
  Address,
  ClientRegisteredVendor,
  CompanyVendor,
  ExemptVendor,
  PrimaryTrade,
  User,
  Vendor
} from 'src/app/classes/models';
import { GenericObject } from 'src/app/interfaces/generic-object';
import { GeoMapLocationItem } from 'src/app/interfaces/geo-map-location-item';
import { ClientVendorSearchModalComponent } from 'src/app/modals/client-vendor-search-modal/client-vendor-search-modal.component';
import { VideoPlayerModalComponent } from 'src/app/modals/video-player-modal/video-player-modal.component';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { CompanyService } from 'src/app/services/company.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { VendorService } from 'src/app/services/vendor.service';

@Component({
  selector: 'app-client-vendors',
  templateUrl: './client-vendors.component.html',
  styleUrls: ['./client-vendors.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ClientVendorsComponent implements OnInit, OnDestroy {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private CompanyVendorService: CompanyVendorService,
    private AuthService: AuthService,
    private CompanyService: CompanyService,
    private ToastService: ToastService,
    private VendorService: VendorService,
    private Router: Router,
    private route: ActivatedRoute,
    private UtilityService: UtilityService,
    private ModalService: NgbModal
  ) {
    const currentNavigationData = this.Router.currentNavigation();
    this.currentNavigationData = currentNavigationData;
    this.LoggedInUser = this.AuthService.getUser();
  }

  title?: string;
  subTitle?: string;
  CompanyId: number | null = null;
  view!: 'client' | 'vive';
  CenteredCoordinate?: [number, number] | null;
  DefaultCoordinate!: [number, number];
  country?: 'US' | 'CA';

  LoggedInUser: User;

  LocationNamesAndCoordinates: GeoMapLocationItem[] = [];

  trades?: PrimaryTrade[];
  showMap: boolean = false;
  currentNavigationData: Navigation | null;
  filterText: string = '';

  CompanyVendorQuery?: GenericObject;
  VendorQuery?: GenericObject;

  clonedVendorQuery?: GenericObject;

  // Query object(s) returned from the grid. If these exist, they're applied to the query
  // loaded in the loadData function.
  CompanyVendorGridQuery?: GenericObject;
  VendorGridQuery?: GenericObject;

  // These store the current request objects being processed by the grid. We compare the values
  // stored in these variables to incoming requests to determine if map data needs to be replaced
  // or concatenated.
  CompanyVendorGridRequest?: IServerSideGetRowsRequest;
  VendorGridRequest?: IServerSideGetRowsRequest;

  Filters: Map<string, any> = new Map();

  FilterModel: FilterModel = {};

  // VPDW-3111 — refs to the two child grid components so the unified
  // toolbar in this template can call into Add vendor / Invite vendor /
  // Add vendor tutorial actions that live on the children.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @ViewChild('clientMyVendors') clientMyVendorsRef?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @ViewChild('clientViveVendors') clientViveVendorsRef?: any;

  get canAddVendor(): boolean {
    const isClientAdmin =
      this.LoggedInUser?.UserAccountRole?.role === 'CLIENT_ADMIN' &&
      this.view === 'client';
    if (!isClientAdmin) {
      return false;
    }

    const company = this.clientMyVendorsRef?.Company;
    // Hide the button until Company flags load, then only show when at
    // least one addable vendor profile type is enabled for this client.
    if (!company) {
      return false;
    }

    return !!(
      company.allowClientRegisteredVendors || company.allowExemptVendors
    );
  }
  get canInviteVendor(): boolean {
    // Vendor invitation is offered on both the "My vendors" page (for
    // non-admin client users) and the "Find new vendors" / VIVE-vendor
    // discovery page (any client user).
    if (this.view === 'vive') return true;
    return (
      this.LoggedInUser?.UserAccountRole?.role !== 'CLIENT_ADMIN' &&
      this.view === 'client'
    );
  }
  get canShowAddVendorTutorial(): boolean {
    return !!this.clientMyVendorsRef?.Company?.allowClientRegisteredVendors;
  }
  onMyVendorsCompanyLoaded(): void {
    this._cdr.markForCheck();
  }

  proxyAddVendor() {
    this.clientMyVendorsRef?.addVendor?.();
  }
  proxyInviteVendor() {
    if (this.view === 'vive') {
      this.clientViveVendorsRef?.inviteVendor?.();
    } else {
      this.clientMyVendorsRef?.inviteVendor?.();
    }
  }
  proxyAddVendorTutorial() {
    this.clientMyVendorsRef?.addVendorTutorialVideo?.();
  }

  findAVendorTutorialVideo() {
    const { componentInstance } = this.ModalService.open(
      VideoPlayerModalComponent,
      { backdrop: 'static', size: 'xl' }
    );

    Object.assign(componentInstance, {
      IFrameOptions: {
        src: 'https://player.vimeo.com/video/1078408425?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
        style: 'width:100%;height:100%;',
        title: 'How to Find a Vendor2'
      }
    });
  }

  displaySimpleSearchModal() {
    const { result, componentInstance } = this.ModalService.open(
      ClientVendorSearchModalComponent,
      { backdrop: 'static', size: 'lg' }
    );

    Object.assign(componentInstance, {
      companyTypeSearchText: 'Vendor Name',
      showTradeSelection: true
    });

    from(result).subscribe({
      next: (VendorSearch: ClientVendorSearchModel) => {
        localStorage.setItem(
          'ClientVendorSearch',
          JSON.stringify(VendorSearch)
        );
        this.initializeFilterInformation();
        this._cdr.markForCheck();
        Object.assign(this, {
          CompanyVendorGridQuery: undefined,
          VendorGridQuery: undefined
        });
        this._cdr.markForCheck();
        this.loadData({ initializeNewQuery: true, filterChanged: true });
      }
    });
  }

  goToVendorSearchPage() {
    this.Router.navigate([
      'client',
      this.CompanyId,
      'vendors',
      'search',
      this.view
    ]);
  }

  clearVendorSearchFilter() {
    localStorage.removeItem('ClientVendorSearch');
    this.filterText = '';
    this.CenteredCoordinate = null;
    this._cdr.markForCheck();
    Object.assign(this, {
      CompanyVendorGridQuery: undefined,
      VendorGridQuery: undefined
    });
    this._cdr.markForCheck();
    this.loadData({ initializeNewQuery: true, filterChanged: true });
  }

  featureClicked(coordinateInfo: GeoMapLocationItem) {
    if (!this.CompanyId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Client Id information.',
        { returnError: true }
      );
    }

    const { dataRecord } = coordinateInfo;

    if (dataRecord) {
      const { id: VendorId } = dataRecord;

      const NavParts: (string | number)[] = [
        'client',
        this.CompanyId,
        'vendors'
      ];

      if (this.view === 'client') {
        NavParts.push('my-vendor');

        if (dataRecord instanceof ExemptVendor) {
          NavParts.push('exemptVendor');
        }

        if (dataRecord instanceof ClientRegisteredVendor) {
          NavParts.push('clientRegisteredVendor');
        }

        NavParts.push(VendorId, 'info');
      }

      if (this.view === 'vive') {
        NavParts.push('vive-vendor', VendorId);
      }

      this.Router.navigate(NavParts);
    }
  }

  // The grid drops its row limit when it reloads to export. The map copies
  // whatever query the grid last built, so it must skip that one: it would
  // fetch the whole vendor list a second time, and later map loads would
  // reuse it.
  private isPagedGridQuery(GridQuery: GenericObject): boolean {
    return GridQuery['options']?.['limit'] !== undefined;
  }

  handleCompanyVendorGridQueryChange({
    GridQuery,
    GridRequest
  }: {
    GridQuery: GenericObject;
    GridRequest: IServerSideGetRowsRequest;
  }) {
    if (!this.isPagedGridQuery(GridQuery)) {
      return;
    }

    this.CompanyVendorGridQuery = GridQuery;
    const loadDataObject = { initializeNewQuery: false, filterChanged: false };
    if (
      this.CompanyVendorGridRequest &&
      JSON.stringify(this.CompanyVendorGridRequest.filterModel) ===
        JSON.stringify(GridRequest.filterModel)
    ) {
      Object.assign(loadDataObject, { filterChanged: false });
    } else {
      Object.assign(loadDataObject, { filterChanged: true });
    }
    this.CompanyVendorGridRequest = GridRequest;
    this.loadData(loadDataObject);
  }

  handleVendorGridQueryChange({
    GridQuery,
    GridRequest
  }: {
    GridQuery: GenericObject;
    GridRequest: IServerSideGetRowsRequest;
  }) {
    if (!this.isPagedGridQuery(GridQuery)) {
      return;
    }

    this.VendorGridQuery = GridQuery;
    const loadDataObject = { initializeNewQuery: false, filterChanged: false };
    if (
      this.VendorGridRequest &&
      JSON.stringify(this.VendorGridRequest.filterModel) ===
        JSON.stringify(GridRequest.filterModel)
    ) {
      Object.assign(loadDataObject, { filterChanged: false });
    } else {
      Object.assign(loadDataObject, { filterChanged: true });
    }
    this.VendorGridRequest = GridRequest;
    this.loadData(loadDataObject);
  }

  handleLocationNamesAndCoordinatesUpdate() {
    const VendorSearchFilterString = localStorage.getItem('ClientVendorSearch');

    if (VendorSearchFilterString) {
      const VendorSearchFilter: ClientVendorSearchModel =
        new ClientVendorSearchModel(JSON.parse(VendorSearchFilterString));
      const {
        address: { name, location }
      } = VendorSearchFilter;

      if (location && name) {
        const { latitude, longitude } = location;
        this.LocationNamesAndCoordinates.push({
          name,
          points: [longitude, latitude],
          src: '/assets/images/geo-alt-fill-red.svg'
        });
      }
    }
  }

  // initializeNewQuery is used for when the grid passes data to this component (perhaps due to filtering).
  // The grid is updated when the suppliedQueryData property changes, but, after the grid updates it sends
  // an event up to this component, instructing this component to initialize a new query (for the map).
  // We can utilize initializeNewQuery to prevent an infinite loop between the grid reloading and this
  // component reloading.
  loadData({
    initializeNewQuery,
    filterChanged
  }: {
    initializeNewQuery: boolean;
    filterChanged: boolean;
  }) {
    if (filterChanged) {
      this.LocationNamesAndCoordinates.splice(0);
    }

    const VendorSearchFilterString = localStorage.getItem('ClientVendorSearch');

    if (this.view === 'client') {
      const CompanyVendorQuery: GenericObject = {
        attributes: [
          'id',
          'vendorLegalName',
          'VendorId',
          'ClientRegisteredVendorId',
          'ExemptVendorId'
        ],
        aliasedAttributes: ['vendorPriorityLevel'],
        Vendor: {
          attributes: ['id', 'legalName'],
          StreetAddress: {
            attributes: [
              'id',
              'address1',
              'address2',
              'country',
              'city',
              'state',
              'zip',
              'latitude',
              'longitude'
            ]
          },
          VendorTrade: {
            options: { separate: true },
            attributes: ['id'],
            PrimaryTrade: {
              attributes: ['id']
            }
          }
        },
        Company: {
          attributes: ['id']
        },
        RecentPendingOrPaidCompanyVendorPayment: {},
        order: [
          ['orderBySpotlightVendorsWithApprovedOrRenewingStatus', 'ASC'],
          ['orderByVendorPriorityLevel', 'ASC'],
          ['orderBySpecificCompanyVendorStatuses'],
          ['viveStatus', 'DESC'],
          ['id', 'DESC']
        ]
      };

      this.clonedVendorQuery = structuredClone(CompanyVendorQuery);

      const currentNavigation = this.currentNavigationData;
      const state = currentNavigation?.extras?.state;

      if (state) {
        const { filters, FilterModel, query } = state;

        if (query) {
          Object.assign(CompanyVendorQuery, query);
        }

        if (FilterModel) {
          Object.assign(this.FilterModel, FilterModel);
        } else if (filters) {
          const { vendorTradeCategories, viveStatus } = filters;

          if (vendorTradeCategories) {
            this.Filters.set('vendorTradeCategories', vendorTradeCategories);
          }

          if (viveStatus) {
            this.Filters.set('viveStatus', viveStatus);
          }
        }
      }

      const applyVendorSpecificQueryData = () => {
        if (!CompanyVendorQuery['$and']) {
          Object.assign(CompanyVendorQuery, { $and: [] });
        }

        const { $and } = CompanyVendorQuery;

        $and.push({
          $or: [
            { VendorId: { $ne: null } },
            { ExemptVendorId: { $ne: null } },
            { ClientRegisteredVendorId: { $ne: null } }
          ]
        });

        Object.assign(CompanyVendorQuery, {
          ExemptVendor: {
            attributes: ['id', 'legalName'],
            StreetAddress: {
              attributes: [
                'id',
                'address1',
                'address2',
                'country',
                'city',
                'state',
                'zip',
                'latitude',
                'longitude'
              ]
            }
          },
          ClientRegisteredVendor: {
            attributes: ['id', 'legalName'],
            StreetAddress: {
              attributes: [
                'id',
                'address1',
                'address2',
                'country',
                'city',
                'state',
                'zip',
                'latitude',
                'longitude'
              ]
            }
          }
        });
      };

      if (VendorSearchFilterString) {
        const VendorSearchFilter: ClientVendorSearchModel =
          new ClientVendorSearchModel(JSON.parse(VendorSearchFilterString));

        const { legalNameOrDba, Trades, TradeCategories, address } =
          VendorSearchFilter;

        if (legalNameOrDba) {
          if (!CompanyVendorQuery['$and']) {
            Object.assign(CompanyVendorQuery, { $and: [] });
          }

          const { $and } = CompanyVendorQuery;
          $and.push({
            $or: [
              { vendorLegalName: { $like: `%${legalNameOrDba}%` } },
              { vendorDba: { $like: `%${legalNameOrDba}%` } }
            ]
          });
        }

        if (Trades.length > 0) {
          const { Vendor } = CompanyVendorQuery;
          Object.assign(Vendor, { options: { required: true } });
          const { VendorTrade } = Vendor;
          const { options, PrimaryTradeId } = VendorTrade;
          const mergedOptions = merge(options || {}, {
            required: true,
            separate: false
          });
          const mergedPrimaryTradeId = merge(PrimaryTradeId || {}, {
            $in: Trades.map((Trade) => Trade.id)
          });
          Object.assign(VendorTrade, {
            options: mergedOptions,
            PrimaryTradeId: mergedPrimaryTradeId
          });
        }

        if (TradeCategories.length > 0) {
          const { Vendor } = CompanyVendorQuery;
          Object.assign(Vendor, { options: { required: true } });
          const { VendorTrade } = Vendor;
          const { options, PrimaryTradeId } = VendorTrade;
          const mergedOptions = merge(options || {}, {
            required: true,
            separate: false
          });

          const PrimaryTradeIds: number[] = [];

          TradeCategories.forEach((TradeCategory) => {
            const { PrimaryTrades } = TradeCategory;

            if (!PrimaryTrades) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Trade category is missing trades.',
                { returnError: true }
              );
            }

            PrimaryTrades.forEach((Trade) => {
              const { id } = Trade;

              if (!id) {
                throw this.ToastService.addErrorToast(
                  'Error',
                  'Trade is missing id.',
                  { returnError: true }
                );
              }

              PrimaryTradeIds.push(id);
            });
          });

          const mergedPrimaryTradeId = merge(PrimaryTradeId || {}, {
            $in: PrimaryTradeIds
          });

          Object.assign(VendorTrade, {
            options: mergedOptions,
            PrimaryTradeId: mergedPrimaryTradeId
          });
        }

        if (address.location) {
          const { location } = address;
          const { latitude, longitude } = location;
          Object.assign(CompanyVendorQuery, {
            vendorDistanceFromPoint: { parameters: { latitude, longitude } }
          });
        }

        // If we're filtering on Trades, Trade Categories or an address location,
        // only process VIVE Vendors. Otherwise, pass in other Vendor types.
        if (
          Trades.length === 0 &&
          TradeCategories.length === 0 &&
          !address.id
        ) {
          applyVendorSpecificQueryData();
        } else {
          Object.assign(CompanyVendorQuery, { VendorId: { $ne: null } });
        }
      } else {
        applyVendorSpecificQueryData();
      }

      if (initializeNewQuery) {
        this.CompanyVendorQuery = CompanyVendorQuery;
      }

      if (this.CompanyVendorGridQuery) {
        merge(CompanyVendorQuery, this.CompanyVendorGridQuery);

        forkJoin([
          this.CompanyVendorService.getCompanyVendors(
            this.UtilityService.stringifyObjectsInQueryObject(
              structuredClone(CompanyVendorQuery)
            )
          ),
          this.CompanyService.getCompany(this.AuthService.getUserCompanyId(), {
            CompanyOperationState: JSON.stringify({
              SubNationalDivision: {}
            })
          })
        ]).subscribe({
          next: ([CompanyVendors, Company]) => {
            const LocationNamesAndCoordinates: {
              name: string;
              points: [number, number];
              additionalText?: string[];
              dataRecord:
                | Vendor
                | ClientRegisteredVendor
                | ExemptVendor
                | null
                | undefined;
            }[] = [];

            if (!Company) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing Client information.',
                { returnError: true }
              );
            }

            CompanyVendors.forEach((CompanyVendor: CompanyVendor) => {
              const {
                Vendor: VendorRecord,
                ClientRegisteredVendor: ClientRegisteredVendorRecord,
                ExemptVendor: ExemptVendorRecord
              } = CompanyVendor;

              if (
                !VendorRecord &&
                !ClientRegisteredVendorRecord &&
                !ExemptVendorRecord
              ) {
                throw this.ToastService.addErrorToast(
                  'Error',
                  'Missing Vendor information.',
                  { returnError: true }
                );
              }

              let legalName: string | undefined = (
                VendorRecord ||
                ClientRegisteredVendorRecord ||
                ExemptVendorRecord
              )?.legalName;
              let StreetAddress: Address | undefined = (
                VendorRecord ||
                ClientRegisteredVendorRecord ||
                ExemptVendorRecord
              )?.StreetAddress;

              if (!legalName) {
                throw this.ToastService.addErrorToast(
                  'Error',
                  'Missing Vendor Legal Name.',
                  { returnError: true }
                );
              }

              if (StreetAddress) {
                const { latitude, longitude } = StreetAddress;

                const LocationNameAndCoordinate: {
                  name: string;
                  points: [number, number];
                  dataRecord:
                    | Vendor
                    | ClientRegisteredVendor
                    | ExemptVendor
                    | null
                    | undefined;
                } = {
                  name: `Vendor: ${legalName}`,
                  points: [Number(longitude), Number(latitude)],
                  dataRecord: null
                };

                if (VendorRecord) {
                  Object.assign(LocationNameAndCoordinate, {
                    dataRecord: new Vendor(VendorRecord)
                  });
                }

                if (ExemptVendorRecord) {
                  Object.assign(LocationNameAndCoordinate, {
                    dataRecord: new ExemptVendor(ExemptVendorRecord)
                  });
                }

                if (ClientRegisteredVendorRecord) {
                  Object.assign(LocationNameAndCoordinate, {
                    dataRecord: new ClientRegisteredVendor(
                      ClientRegisteredVendorRecord
                    )
                  });
                }

                LocationNamesAndCoordinates.push(LocationNameAndCoordinate);
              }
            });

            Object.assign(this, {
              LocationNamesAndCoordinates: [
                ...this.LocationNamesAndCoordinates,
                ...LocationNamesAndCoordinates
              ]
            });
            this.handleLocationNamesAndCoordinatesUpdate();
          }
        });
      }
    }

    if (this.view === 'vive') {
      const VendorQuery: GenericObject = {
        options: { subQuery: false },
        aliasedAttributes: ['sharedVendorId', 'vendorTrades'],
        nonClientVendors: { parameters: { CompanyId: this.CompanyId } },
        sharedStates: { parameters: { CompanyId: this.CompanyId } },
        isApprovedWithAtLeastOneActiveClient: { $eq: true },
        ComplianceContact: {
          options: { required: true }
        },
        InvitationContact: {},
        StreetAddress: {
          attributes: [
            'address1',
            'address2',
            'city',
            'state',
            'zip',
            'latitude',
            'longitude'
          ]
        },
        country: this.country
      };

      this.clonedVendorQuery = structuredClone(VendorQuery);

      if (VendorSearchFilterString) {
        const VendorSearchFilter: ClientVendorSearchModel =
          new ClientVendorSearchModel(JSON.parse(VendorSearchFilterString));
        const { legalNameOrDba, Trades, TradeCategories, address } =
          VendorSearchFilter;

        if (legalNameOrDba) {
          if (!VendorQuery['$and']) {
            Object.assign(VendorQuery, { $and: [] });
          }

          const { $and } = VendorQuery;
          $and.push({
            $or: [
              { legalName: { $like: `%${legalNameOrDba}%` } },
              { dba: { $like: `%${legalNameOrDba}%` } }
            ]
          });
        }

        if (Trades.length > 0) {
          if (!VendorQuery['VendorTrade']) {
            Object.assign(VendorQuery, { VendorTrade: {} });
          }

          const { VendorTrade } = VendorQuery;
          const { options, PrimaryTradeId } = VendorTrade;
          const mergedOptions = merge(options || {}, { required: true });
          const mergedPrimaryTradeId = merge(PrimaryTradeId || {}, {
            $in: Trades.map((Trade) => Trade.id)
          });
          Object.assign(VendorTrade, {
            options: mergedOptions,
            PrimaryTradeId: mergedPrimaryTradeId
          });
        }

        if (TradeCategories.length > 0) {
          if (!VendorQuery['VendorTrade']) {
            Object.assign(VendorQuery, { VendorTrade: {} });
          }
          const { VendorTrade } = VendorQuery;

          const { options, PrimaryTradeId } = VendorTrade;
          const mergedOptions = merge(options || {}, { required: true });

          const PrimaryTradeIds: number[] = [];

          TradeCategories.forEach((TradeCategory) => {
            const { PrimaryTrades } = TradeCategory;

            if (!PrimaryTrades) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Trade category is missing trades.',
                { returnError: true }
              );
            }

            PrimaryTrades.forEach((Trade) => {
              const { id } = Trade;

              if (!id) {
                throw this.ToastService.addErrorToast(
                  'Error',
                  'Trade is missing id.',
                  { returnError: true }
                );
              }

              PrimaryTradeIds.push(id);
            });
          });

          const mergedPrimaryTradeId = merge(PrimaryTradeId || {}, {
            $in: PrimaryTradeIds
          });

          Object.assign(VendorTrade, {
            options: mergedOptions,
            PrimaryTradeId: mergedPrimaryTradeId
          });
        }

        if (address.location) {
          const { location } = address;
          const { latitude, longitude } = location;
          Object.assign(VendorQuery, {
            vendorDistanceFromPoint: { parameters: { latitude, longitude } }
          });
        }
      }

      if (initializeNewQuery) {
        this.VendorQuery = VendorQuery;
      }

      if (this.VendorGridQuery) {
        merge(VendorQuery, this.VendorGridQuery);

        this.VendorService.getVendors(
          this.UtilityService.stringifyObjectsInQueryObject(
            structuredClone(VendorQuery)
          )
        ).subscribe({
          next: (Vendors) => {
            const LocationNamesAndCoordinates: GeoMapLocationItem[] = [];

            Vendors.forEach((Vendor: Vendor) => {
              if (!Vendor) {
                throw this.ToastService.addErrorToast(
                  'Error',
                  'Missing Vendor information.',
                  { returnError: true }
                );
              }

              const { legalName, StreetAddress } = Vendor;

              if (!legalName) {
                throw this.ToastService.addErrorToast(
                  'Error',
                  'Missing Vendor Legal Name.',
                  { returnError: true }
                );
              }

              if (StreetAddress) {
                const { latitude, longitude } = StreetAddress;

                const LocationNameAndCoordinate: {
                  name: string;
                  points: [number, number];
                  dataRecord: Vendor | null | undefined;
                } = {
                  name: `Vendor: ${legalName}`,
                  points: [Number(longitude), Number(latitude)],
                  dataRecord: Vendor
                };

                LocationNamesAndCoordinates.push(LocationNameAndCoordinate);
              }
            });

            Object.assign(this, {
              LocationNamesAndCoordinates: [
                ...this.LocationNamesAndCoordinates,
                ...LocationNamesAndCoordinates
              ]
            });
            this.handleLocationNamesAndCoordinatesUpdate();
          }
        });
      }
    }
  }

  initializeFilterInformation() {
    const VendorSearchFilterString = localStorage.getItem('ClientVendorSearch');

    if (VendorSearchFilterString) {
      const VendorSearchFilter = new ClientVendorSearchModel(
        JSON.parse(VendorSearchFilterString)
      );

      const { legalNameOrDba, Trades, TradeCategories, address } =
        VendorSearchFilter;

      const FilterParts: string[] = [];

      if (legalNameOrDba) {
        FilterParts.push('Vendor Name:', `"${legalNameOrDba}"`, '/');
      }

      if (Trades && Trades.length > 0) {
        FilterParts.push('Trades:');

        const TradeParts: string[] = [];
        Trades.forEach((Trade) => {
          const { name } = Trade;
          if (name) {
            TradeParts.push(name, '-');
          }
        });

        TradeParts.pop();

        FilterParts.push(`"${TradeParts.join(' ')}"`);
        FilterParts.push('/');
      }

      if (TradeCategories && TradeCategories.length > 0) {
        FilterParts.push('Trade Categories:');

        const TradeCategoryParts: string[] = [];
        TradeCategories.forEach((TradeCategory) => {
          const { categoryName } = TradeCategory;
          if (categoryName) {
            TradeCategoryParts.push(categoryName, '-');
          }
        });

        TradeCategoryParts.pop();

        FilterParts.push(`"${TradeCategoryParts.join(' ')}"`);
        FilterParts.push('/');
      }

      // Need to utilize coordinates for finding Vendors.
      if (address) {
        const { name, location } = address;

        if (name) {
          FilterParts.push('Work Address:', `"${name}"`, '/');
        }

        if (location) {
          const { latitude, longitude } = location;
          this.CenteredCoordinate = [longitude, latitude];
        }
      }

      FilterParts.pop();

      this.filterText = FilterParts.join(' ');
    }
    this._cdr.markForCheck();
  }

  ngOnInit(): void {
    const CompanyId = this.AuthService.getUserCompanyId();

    if (!CompanyId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Company Id.', {
        returnError: true
      });
    }

    this.initializeFilterInformation();

    this.CompanyId = CompanyId;

    const { Company } = this.LoggedInUser;

    if (!Company) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Client user information.',
        { returnError: true }
      );
    }

    const { country } = Company;

    if (!country) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing country information.',
        { returnError: true }
      );
    }

    this.country = country;

    const CountryToCoordinateMap: {
      US: [number, number];
      CA: [number, number];
    } = {
      US: [-95.7129, 37.0902],
      CA: [-106.3468, 56.1304]
    };

    this.DefaultCoordinate = CountryToCoordinateMap[this.country];

    this.route.paramMap.subscribe({
      next: (ParamMap) => {
        const view = ParamMap.get('view');

        if (typeof view !== 'string' || !['client', 'vive'].includes(view)) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Invalid view parameter.',
            { returnError: true }
          );
        }

        if (view === 'client') {
          Object.assign(this, { title: 'My Vendors', subTitle: '' });
        }

        if (view === 'vive') {
          Object.assign(this, {
            title: 'Find New Vendors',
            subTitle: 'Vendors available for registration with your company'
          });
        }

        Object.assign(this, { view });
        this._cdr.markForCheck();
        this.loadData({ initializeNewQuery: true, filterChanged: true });
      }
    });
  }

  ngOnDestroy(): void {
    localStorage.removeItem('ClientVendorSearch');
  }
}
